import React, { Component, Fragment } from 'react';
import { Type } from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';
import tokens from '@spotify-internal/encore-foundation';
import { AnimatedAlert } from '../../Shared/AnimatedAlert';
import { CONFLICT, ERROR } from '../../../store/selectors';
import { trackEvent } from '../../../lib/Tracking';
import { PODCAST_SUPPORT_EMAIL } from '../../../lib/constants';
import { get } from 'lodash';
import { Country, Language, Network, Category, SelectedCategory } from '../../../types';
import { PodcastModalBody } from '../../Shared/PodcastModal';
import { FormattedMessage } from 'react-intl';

const FIELD_INDICATOR = {
  country: (
    <FormattedMessage
      id="add-podcast.review-country-field"
      defaultMessage="Where your podcast is made"
    />
  ),
  language: (
    <FormattedMessage
      id="add-podcast.review-language-field"
      defaultMessage="Primary language spoken"
    />
  ),
  aggregator: (
    <FormattedMessage
      id="add-podcast.review-hosting-provider-field"
      defaultMessage="Your hosting provider"
    />
  ),
};

const ItemContainer = styled.div`
  border-top: 1px solid #bdbdbd;
`;

const Item = styled.div`
  align-items: center;
  border-bottom: 1px solid #bdbdbd;
  padding: ${tokens.spacer16} 0;
  display: flex;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: start;
  }
`;

const StyledIndicator = styled(Type.p)`
  margin-left: ${tokens.spacer8};
  margin-right: ${tokens.spacer32};
  width: 200px;

  @media (max-width: 480px) {
    margin: 0;
  }
`;

interface Props {
  country?: Country;
  language?: Language;
  aggregator?: Network;
  categories?: SelectedCategory[];
  status: 'UNAUTHORIZED' | 'CONFLICT' | 'LOADING' | 'ERROR' | 'SUBMIT_SUCCESS' | undefined;
  shouldShowAggregators: boolean;
}

export class ReviewAndSubmit extends Component<Props> {
  componentDidMount() {
    trackEvent({ category: 'page-type', action: 'view', label: 'submit-metadata-confirmation' });
  }

  /* istanbul ignore next */
  shouldComponentUpdate(nextProps: Props) {
    return Boolean(nextProps.categories);
  }

  renderItem = (key: string, value: string) => (
    <Item>
      <StyledIndicator variant={Type.body2} color="gray60" condensed>
        {FIELD_INDICATOR[key]}
      </StyledIndicator>
      <Type as="p" condensed>
        {value}
      </Type>
    </Item>
  );

  renderCategory = (category: SelectedCategory) => (
    <Fragment key={category.categoryKey}>
      <Item>
        <StyledIndicator variant={Type.body2} color="gray60" condensed>
          {category.categoryKey === 'primaryCategory' ? (
            <FormattedMessage
              id="add-podcast.review-primary-category-title"
              defaultMessage="Primary category"
            />
          ) : (
            <FormattedMessage
              id="add-podcast.review-additional-category-title"
              defaultMessage="Additional category"
            />
          )}
        </StyledIndicator>
        <Type as="p" condensed>
          {category.name}
        </Type>
      </Item>
      <Item>
        <StyledIndicator variant={Type.body2} color="gray60" condensed>
          <FormattedMessage
            id="add-podcast.review-sub-category-title"
            defaultMessage="Sub-category"
          />
        </StyledIndicator>
        <Type as="p" condensed>
          {category.subcategories.length ? category.subcategories.map(s => s.name).join(', ') : '-'}
        </Type>
      </Item>
    </Fragment>
  );

  render() {
    if (this.props.status === CONFLICT)
      return (
        <PodcastModalBody data-qa="review-and-submit-error">
          <Type as="h1" variant={Type.heading2}>
            <FormattedMessage
              id="add-podcast.review-duplicate-error"
              defaultMessage="This podcast is already on Spotify."
            />
          </Type>
          <Type as="p" variant={Type.body1}>
            <FormattedMessage
              id="add-podcast.review-get-help"
              defaultMessage="Contact your Spotify podcast account manager or reach us at {email}."
              values={{
                email: (
                  <strong>
                    <a href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{PODCAST_SUPPORT_EMAIL}</a>
                  </strong>
                ),
              }}
            />
          </Type>
        </PodcastModalBody>
      );
    return (
      <Fragment>
        {this.props.status === ERROR && (
          <AnimatedAlert
            data-qa="error-alert"
            message={
              <FormattedMessage
                id="add-podcast.review-general-error"
                defaultMessage="Unable to submit your podcast. Wait a few minutes, then try again."
              />
            }
          />
        )}
        <PodcastModalBody data-qa="review-and-submit">
          <Type as="h1" variant={Type.heading2}>
            <FormattedMessage
              id="add-podcast.review-title"
              defaultMessage="Does everything look OK?"
            />
          </Type>
          <Type as="p" variant={Type.body1}>
            {
              <FormattedMessage
                id="add-podcast.review-instructions"
                defaultMessage="You won't be able to edit this once you hit <strong>submit</strong>"
                values={{
                  strong: (...chunks) => <strong>{chunks}</strong>,
                }}
              />
            }
            .
          </Type>
          <ItemContainer>
            {this.props.country && this.renderItem('country', this.props.country.name)}
            {this.props.language && this.renderItem('language', this.props.language.name)}
            {this.props.shouldShowAggregators &&
              this.props.aggregator &&
              this.renderItem('aggregator', this.props.aggregator.name)}
            {/* @TODO: Find the root cause of this being undefined. */
            (get(this.props, 'categories', []) as SelectedCategory[]).map(this.renderCategory)}
          </ItemContainer>
        </PodcastModalBody>
      </Fragment>
    );
  }
}
